"use client";

import dynamic from "next/dynamic";
import { docsHref } from "@/lib/docs-links";
import { SOLUTION_LINKS } from "./solution-links";
import type { CSSProperties, ReactNode } from "react";
import {
  ACCENT_TEXT,
  DISPLAY,
  EDITORIAL_DARK_SURFACE,
  EDITORIAL_CONTAINER_CLS,
  GROUND,
  HOME_DEFAULT_THEME_ID,
  INK,
  LABEL_CLS,
  PAGE_TITLE_CLS,
  mix,
  SURFACE_DARK,
} from "./theme";
import { EditorialThemeProvider } from "./theme-context";
import { oneSystemClass, oneSystemStyle } from "./one-system";
import { EditorialNav } from "./nav";
import { HOW_IT_WORKS_HREF, NEWSLETTER_HREF, PARTNER_HREF, WAITLIST_HREF, WHY_A_TWIN_HREF } from "./site-links";
import { EditorialFooter, type FooterColumn } from "./footer";
import { Grain } from "./primitives";
import type { Cta } from "./types";

/**
 * Loaded lazily, and always mounted: the activation conversation is the primary
 * conversion on all deployments. Opens on any click of an
 * `a[href="#waitlist"]` (i.e. WAITLIST_HREF).
 */
const WaitlistDialog = dynamic(
  () =>
    import("@/components/editorial/waitlist-dialog").then(
      (module) => module.WaitlistDialog,
    ),
  { ssr: false },
);

const NewsletterDialog = dynamic(
  () =>
    import("@/components/editorial/newsletter-dialog").then(
      (module) => module.NewsletterDialog,
    ),
  { ssr: false },
);

export { NEWSLETTER_HREF, PARTNER_HREF, WAITLIST_HREF } from "./site-links";

/* One site-wide nav CTA + footer config, shared by every editorial page. The
 * nav's grouped menus live in nav-menu.tsx. */
const SITE_CTA: Cta = {
  label: "Discuss your first workflow",
  href: WAITLIST_HREF,
};
const SITE_FOOTER_COLUMNS: FooterColumn[] = [
  {
    heading: "Product",
    links: [
      { label: "Why a Twin", href: WHY_A_TWIN_HREF },
      { label: "How it works", href: HOW_IT_WORKS_HREF },
      { label: "Pricing", href: "/pricing" },
      { label: "Changelog", href: "/changelog" },
      { label: "Research", href: "/research" },
      { label: "FAQ", href: "/faq" },
    ],
  },
  {
    // The nav's Solutions menu, from the same list.
    heading: "Solutions",
    links: SOLUTION_LINKS.map(({ label, href }) => ({ label, href })),
  },
  {
    heading: "Company",
    links: [
      { label: "About", href: "/about" },
      { label: "Case studies", href: "/case-studies" },
      { label: "Blog", href: "/blog" },
      { label: "Research briefing", href: NEWSLETTER_HREF },
      { label: "Become a partner", href: PARTNER_HREF },
      { label: "Mercury Labs", href: "https://mercurylabs.io" },
    ],
  },
  {
    heading: "Support & legal",
    links: [
      { label: "Support", href: docsHref("/support") },
      { label: "Legal & Security", href: docsHref("/policies") },
      { label: "Privacy", href: docsHref("/policies/privacy") },
      { label: "Terms", href: docsHref("/policies/terms") },
      { label: "Sub-processors", href: "/subprocessors" },
      { label: "Cookie settings", href: "#cookie-settings" },
    ],
  },
];

/**
 * Site chrome wrapper — theme provider + nav + footer — shared by the
 * home and every sub-page so the editorial nav/footer are defined once.
 * `solidNav` forces the cream nav from the top (pages without a
 * full-height dark hero); leave false for the home's scroll-aware nav.
 *
 * Every page it wraps, nav and footer included, is drawn in the
 * application's own design system with its claret theme and the site's own
 * type (one-system.tsx, `site`), so a page and the product shots on it read
 * as one, and one page reads like the next. The home took the look first;
 * the rest of the site followed it on 2026-10-01. The palette picker has
 * nothing to re-ink there, so the nav is not given it.
 */
export function EditorialShell({
  children,
  solidNav = false,
  provideTheme = true,
  dark = false,
}: {
  children: ReactNode;
  solidNav?: boolean;
  provideTheme?: boolean;
  dark?: boolean;
}) {
  const shellStyle = {
    ...DISPLAY,
    ...oneSystemStyle("site"),
    // A dark page keeps the editorial night under the system's tokens.
    ...(dark ? { "--theme-surface-dark": EDITORIAL_DARK_SURFACE, backgroundColor: SURFACE_DARK, color: GROUND } : {}),
  } as CSSProperties;

  const shell = (
    <div className={`antialiased ${oneSystemClass("claret", "site")}`} data-editorial="" style={shellStyle}>
      <a
        href="#main-content"
        className="fixed top-2 left-2 z-[100] -translate-y-20 bg-[var(--theme-surface-dark)] px-4 py-3 text-sm font-semibold text-[var(--theme-ground)] transition-transform focus:translate-y-0 motion-reduce:transition-none"
      >
        Skip to content
      </a>
      <EditorialNav
        wordmark="MLX"
        cta={SITE_CTA}
        solidFromStart={solidNav}
        dark={dark}
      />
      {/* The single <main> landmark for every editorial page + skip-link
          target. The home and /concepts render their content as a plain
          <div> so this stays the one main region per page. Bare element (no
          className) so it is layout-neutral — <main> is display:block. */}
      <main id="main-content">{children}</main>
      <WaitlistDialog />
      <NewsletterDialog />
      <EditorialFooter
        dark={dark}
        wordmark="MLX"
        blurb="Your Twin connects business records, relationships and rules into a shared understanding for your team and AI tools. Built by Mercury Labs."
        columns={SITE_FOOTER_COLUMNS}
        legal="© 2026 Mercury Labs. MLX is a Mercury Labs product. All rights reserved."
      />
    </div>
  );

  if (!provideTheme) return shell;
  return (
    <EditorialThemeProvider defaultThemeId={HOME_DEFAULT_THEME_ID}>
      {shell}
    </EditorialThemeProvider>
  );
}

/**
 * Lightweight page header for sub-pages — section marker + display title
 * + optional lead, on the cream ground, with top padding to clear the
 * fixed nav. (The home uses the full HeroSection instead.)
 */
export function PageHeader({
  marker,
  title,
  lead,
  dark = false,
  anchorId = "top",
}: {
  /** Optional context label; omit it when it would only repeat the page name. */
  marker?: string;
  title: string;
  lead?: string;
  dark?: boolean;
  /** Override when multiple page-header concepts share one review page. */
  anchorId?: string;
}) {
  const bg = dark ? SURFACE_DARK : GROUND;
  const fg = dark ? GROUND : INK;
  const accent = dark ? "var(--theme-accent)" : ACCENT_TEXT;

  return (
    <section
      id={anchorId}
      className="relative overflow-hidden"
      style={{ backgroundColor: bg, color: fg }}
    >
      <Grain opacity={dark ? 0.5 : 0.45} />
      <div className={`relative z-10 pt-32 pb-12 sm:pt-36 sm:pb-14 md:pb-16 ${EDITORIAL_CONTAINER_CLS}`}>
        {marker ? (
          <div className="mb-8 flex items-center gap-3">
            <span
              className="h-2 w-2"
              style={{ backgroundColor: accent }}
              aria-hidden="true"
            />
            <span className={LABEL_CLS} style={{ color: accent }}>
              {marker}
            </span>
          </div>
        ) : null}
        <h1
          style={{ ...DISPLAY, color: fg }}
          className={`max-w-[20ch] ${PAGE_TITLE_CLS}`}
        >
          {title}
        </h1>
        {lead && (
          <p
            className="mt-6 max-w-[58ch] text-[1.1rem] leading-[1.6] md:text-[1.25rem]"
            style={{
              color: dark ? mix("--theme-ground", 88) : mix("--theme-ink", 88),
            }}
          >
            {lead}
          </p>
        )}
      </div>
    </section>
  );
}
